import React, { useEffect, useRef, useState } from 'react';
import * as BABYLON from '@babylonjs/core';

/**
 * Architectural Projection-Mapping scene for the landing page.
 *
 * Shows a stylised cathedral facade (main nave + two corner towers + central
 * dome + small spires) being mapped from 3 ground-based projectors. Wireframe
 * edges render over the geometry for the "3D CAD + projection" aesthetic from
 * the user's reference image. Blue volumetric cones fly from each projector to
 * the facade; emissive blue patches on the building simulate the projected
 * content itself.
 */
const LandingBuildingScene = ({ className = '' }) => {
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const visibleRef = useRef(true);

  useEffect(() => {
    if (!canvasRef.current) return;
    let disposed = false;

    try {
      const engine = new BABYLON.Engine(canvasRef.current, true, {
        preserveDrawingBuffer: false, stencil: false, antialias: true, powerPreference: 'low-power'
      });
      engine.setHardwareScalingLevel(1.25);
      const scene = new BABYLON.Scene(engine);
      scene.clearColor = new BABYLON.Color4(0.01, 0.015, 0.03, 1);
      scene.ambientColor = new BABYLON.Color3(0.06, 0.08, 0.12);

      const camera = new BABYLON.ArcRotateCamera('camB', Math.PI / 2.9, Math.PI / 2.4, 26, new BABYLON.Vector3(0, 3, 0), scene);
      camera.minZ = 0.1;
      camera.lowerRadiusLimit = camera.upperRadiusLimit = 26;
      scene.activeCamera = camera;

      const hemi = new BABYLON.HemisphericLight('h', new BABYLON.Vector3(0, 1, 0), scene);
      hemi.intensity = 0.25;
      hemi.groundColor = new BABYLON.Color3(0.02, 0.03, 0.05);

      /* ─── Grid floor with cyan lines ─── */
      const lines = [];
      for (let i = -18; i <= 18; i++) {
        lines.push([new BABYLON.Vector3(i, 0.01, -18), new BABYLON.Vector3(i, 0.01, 18)]);
        lines.push([new BABYLON.Vector3(-20, 0.01, i), new BABYLON.Vector3(20, 0.01, i)]);
      }
      const grid = BABYLON.MeshBuilder.CreateLineSystem('grid', { lines }, scene);
      grid.color = new BABYLON.Color3(0.08, 0.42, 0.7);
      grid.alpha = 0.25;

      /* ─── Cathedral materials ─── */
      const stoneMat = new BABYLON.StandardMaterial('stone', scene);
      stoneMat.diffuseColor = new BABYLON.Color3(0.08, 0.09, 0.12);
      stoneMat.specularColor = new BABYLON.Color3(0.05, 0.07, 0.1);
      stoneMat.emissiveColor = new BABYLON.Color3(0.02, 0.03, 0.05);

      const projLit = new BABYLON.StandardMaterial('projLit', scene);
      projLit.diffuseColor = new BABYLON.Color3(0.05, 0.1, 0.2);
      projLit.emissiveColor = new BABYLON.Color3(0.12, 0.5, 0.95);
      projLit.specularColor = BABYLON.Color3.Black();

      const addEdges = (mesh, color = new BABYLON.Color4(0.5, 0.85, 1.0, 0.8)) => {
        mesh.enableEdgesRendering(0.9);
        mesh.edgesWidth = 1.4;
        mesh.edgesColor = color;
      };

      /* ─── Central nave ─── */
      const nave = BABYLON.MeshBuilder.CreateBox('nave', { width: 10, height: 5.5, depth: 5 }, scene);
      nave.position = new BABYLON.Vector3(0, 2.75, 0);
      nave.material = stoneMat;
      addEdges(nave);

      // Emissive blue projection patch on the front of the nave
      const naveFront = BABYLON.MeshBuilder.CreatePlane('naveFront', { width: 8.5, height: 4, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
      naveFront.position = new BABYLON.Vector3(0, 2.75, -2.51);
      naveFront.material = projLit;

      /* ─── Roof ridge on nave ─── */
      const ridge = BABYLON.MeshBuilder.CreateBox('ridge', { width: 10, height: 0.8, depth: 3.5 }, scene);
      ridge.position = new BABYLON.Vector3(0, 5.95, 0);
      ridge.material = stoneMat;
      addEdges(ridge);

      /* ─── Corner towers ─── */
      const makeTower = (x) => {
        const base = BABYLON.MeshBuilder.CreateBox('tower', { width: 3, height: 7, depth: 3 }, scene);
        base.position = new BABYLON.Vector3(x, 3.5, 0);
        base.material = stoneMat;
        addEdges(base);

        // Projection-lit band on the tower front
        const band = BABYLON.MeshBuilder.CreatePlane('band', { width: 2.4, height: 2.8, sideOrientation: BABYLON.Mesh.DOUBLESIDE }, scene);
        band.position = new BABYLON.Vector3(x, 3.3, -1.51);
        band.material = projLit;

        const cap = BABYLON.MeshBuilder.CreateBox('cap', { width: 3.3, height: 0.6, depth: 3.3 }, scene);
        cap.position = new BABYLON.Vector3(x, 7.3, 0);
        cap.material = stoneMat;
        addEdges(cap);

        // Spire on top
        const spire = BABYLON.MeshBuilder.CreateCylinder('spire', { height: 2, diameterTop: 0, diameterBottom: 1.4, tessellation: 8 }, scene);
        spire.position = new BABYLON.Vector3(x, 8.6, 0);
        spire.material = stoneMat;
        addEdges(spire);
      };
      makeTower(-6.5);
      makeTower(6.5);

      /* ─── Central dome ─── */
      const drum = BABYLON.MeshBuilder.CreateCylinder('drum', { height: 1.8, diameter: 4.5, tessellation: 16 }, scene);
      drum.position = new BABYLON.Vector3(0, 6.7, 0);
      drum.material = stoneMat;
      addEdges(drum);

      const dome = BABYLON.MeshBuilder.CreateSphere('dome', { diameter: 4.2, slice: 0.5, segments: 20 }, scene);
      dome.position = new BABYLON.Vector3(0, 7.6, 0);
      const domeMat = new BABYLON.StandardMaterial('domeMat', scene);
      domeMat.diffuseColor = new BABYLON.Color3(0.1, 0.14, 0.22);
      domeMat.emissiveColor = new BABYLON.Color3(0.06, 0.3, 0.6);
      domeMat.specularColor = new BABYLON.Color3(0.2, 0.3, 0.5);
      dome.material = domeMat;
      addEdges(dome, new BABYLON.Color4(0.6, 0.95, 1.0, 0.9));

      const topSpire = BABYLON.MeshBuilder.CreateCylinder('topSpire', { height: 1.5, diameterTop: 0, diameterBottom: 0.8, tessellation: 12 }, scene);
      topSpire.position = new BABYLON.Vector3(0, 10.4, 0);
      topSpire.material = stoneMat;
      addEdges(topSpire);

      /* ─── Small decorative pinnacles along the parapet ─── */
      for (let i = -1; i <= 1; i++) {
        if (i === 0) continue;
        const p = BABYLON.MeshBuilder.CreateCylinder(`pin_${i}`, { height: 1, diameterTop: 0, diameterBottom: 0.4, tessellation: 6 }, scene);
        p.position = new BABYLON.Vector3(i * 3.6, 6.1, 2.5);
        p.material = stoneMat;
        addEdges(p);
      }

      /* ─── Entrance arch (darker rectangle inset) ─── */
      const arch = BABYLON.MeshBuilder.CreatePlane('arch', { width: 2.2, height: 2.8 }, scene);
      arch.position = new BABYLON.Vector3(0, 1.6, -2.52);
      const archMat = new BABYLON.StandardMaterial('archMat', scene);
      archMat.diffuseColor = new BABYLON.Color3(0, 0, 0);
      archMat.emissiveColor = new BABYLON.Color3(0.02, 0.05, 0.12);
      archMat.disableLighting = true;
      arch.material = archMat;

      /* ─── Ground projectors (3 units on the ground plane) ─── */
      const projMat = new BABYLON.PBRMaterial('projMat2', scene);
      projMat.albedoColor = new BABYLON.Color3(0.06, 0.07, 0.09);
      projMat.metallic = 0.4; projMat.roughness = 0.5;

      const projSlots = [
        { pos: new BABYLON.Vector3(-10, 0.6, 8), aim: new BABYLON.Vector3(-6.5, 3.5, -1) },
        { pos: new BABYLON.Vector3(  0, 0.6, 10), aim: new BABYLON.Vector3( 0, 3, -1) },
        { pos: new BABYLON.Vector3( 10, 0.6, 8), aim: new BABYLON.Vector3( 6.5, 3.5, -1) }
      ];

      projSlots.forEach((p, i) => {
        const body = BABYLON.MeshBuilder.CreateBox(`gpb_${i}`, { width: 1.4, height: 1.0, depth: 1.8 }, scene);
        body.position = p.pos;
        body.material = projMat;

        // Lens glow
        const lens = BABYLON.MeshBuilder.CreateDisc(`gpl_${i}`, { radius: 0.28, tessellation: 20 }, scene);
        const lensMat = new BABYLON.StandardMaterial(`glm_${i}`, scene);
        lensMat.emissiveColor = new BABYLON.Color3(0.3, 0.75, 1.0);
        lensMat.disableLighting = true;
        lens.material = lensMat;
        const dir = p.aim.subtract(p.pos).normalize();
        lens.position = p.pos.add(dir.scale(0.95));
        const up = new BABYLON.Vector3(0, 0, 1);
        const lensQ = BABYLON.Quaternion.FromUnitVectorsToRef(up, dir, new BABYLON.Quaternion());
        lens.rotationQuaternion = lensQ;

        // Volumetric beam cone
        const delta = p.aim.subtract(p.pos);
        const dist = delta.length();
        const beam = BABYLON.MeshBuilder.CreateCylinder(`gb_${i}`, {
          height: dist + 1, diameterTop: 0.35, diameterBottom: 5.5, tessellation: 4
        }, scene);
        const cylUp = new BABYLON.Vector3(0, 1, 0);
        const q = BABYLON.Quaternion.FromUnitVectorsToRef(cylUp, dir.scale(-1), new BABYLON.Quaternion());
        beam.rotationQuaternion = q;
        const mid = p.pos.add(dir.scale((dist + 1) / 2));
        beam.position = mid;
        const bm = new BABYLON.StandardMaterial(`gbm_${i}`, scene);
        bm.emissiveColor = new BABYLON.Color3(0.1, 0.55, 1.0);
        bm.diffuseColor = BABYLON.Color3.Black();
        bm.specularColor = BABYLON.Color3.Black();
        bm.alpha = 0.18;
        bm.disableLighting = true;
        bm.backFaceCulling = false;
        beam.material = bm;
        beam.alphaIndex = 10 + i;
      });

      /* ─── Slow auto-orbit + visibility pause ─── */
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => { visibleRef.current = e.isIntersecting; });
      }, { threshold: 0.05 });
      io.observe(canvasRef.current);

      const t0 = performance.now();
      let last = 0;
      const FRAME = 1000 / 30;
      const loop = (now) => {
        if (disposed) return;
        requestAnimationFrame(loop);
        if (!visibleRef.current) return;
        if (now - last < FRAME) return;
        last = now;
        const t = (now - t0) / 1000;
        camera.alpha = Math.PI / 2.9 + Math.sin(t * 0.18) * 0.2;
        scene.render();
      };
      requestAnimationFrame(loop);

      const onResize = () => engine.resize();
      window.addEventListener('resize', onResize);

      return () => {
        disposed = true;
        window.removeEventListener('resize', onResize);
        io.disconnect();
        try { scene.dispose(); } catch (e) { /* noop */ }
        try { engine.dispose(); } catch (e) { /* noop */ }
      };
    } catch (err) {
      console.warn('Building scene failed:', err);
      setFailed(true);
    }
  }, []);

  if (failed) return null;

  return (
    <canvas
      ref={canvasRef}
      className={className}
      data-testid="landing-building-canvas"
      style={{ display: 'block', width: '100%', height: '100%', outline: 'none' }}
    />
  );
};

export default LandingBuildingScene;
